import { environment } from '@coin/shared/core/util-environments';
import { basicCmsEmail } from '../constants/basic-cms-email';
import { CmsContentItemContent } from '../models/cms-content-item.model';
import { CmsItem } from '../models/cms-item.model';
import { CmsEmailIndexListElementHelper } from './cms-email-index-list-element.helper';

export class TransformEmailHtmlHelper {
  public static readonly defaultPadding = 'padding-left: 30px; padding-right: 30px;';
  public static readonly fullWidth = 600;
  public static readonly defaultItemFullWidth = 530;
  public static readonly fontFamily = 'font-family: Arial, Calibri, sans-serif !important;';
  public static readonly headlineContainerId = 'headline-container';

  public static setupEmailHtml(emailTemplateId: string, previewCmsEmail: CmsContentItemContent): string {
    let basicEmailBody = basicCmsEmail.toString();
    const { textBody, footer } = TransformEmailHtmlHelper.tranformContentElementsToMailHtml(previewCmsEmail);

    if (previewCmsEmail.sideData['no-header-image']) {
      basicEmailBody = basicEmailBody.replace(
        ` <img alt="header-image" width="600" style="line-height: 0px; width: 600px; max-width: 600px; vertical-align: bottom;" src="$|EMAIL_IMAGE|">`,
        ''
      );
    } else {
      basicEmailBody = basicEmailBody.replace('$|EMAIL_IMAGE|', previewCmsEmail.otherImage);
    }

    const headlineBody = this.getHeadline(previewCmsEmail);
    const headerLogo = this.getHeaderLogo(previewCmsEmail);
    basicEmailBody = basicEmailBody.replace('$|TEMPLATE_ID|', emailTemplateId);
    basicEmailBody = basicEmailBody.replace('$|DATE_TEXT|', previewCmsEmail.sideData.datePartText as string);
    basicEmailBody = basicEmailBody.replace('$|MAIN_HEADLINE|', headlineBody);
    basicEmailBody = basicEmailBody.replace('$|HEADER_LOGO|', headerLogo);
    basicEmailBody = basicEmailBody.replace('$|HEADLINE_CONTAINER_ID|', TransformEmailHtmlHelper.headlineContainerId);
    basicEmailBody = basicEmailBody.replace('$|EMAIL_CONTENT|', textBody);
    basicEmailBody = basicEmailBody.replace('$|FOOTER_CONTENT|', footer);
    basicEmailBody = basicEmailBody.replaceAll('<a href', '<a style="color: #5E328B;" href');
    // replace all '&nbsp;' that are not alone in an element, workaround for https://github.com/slab/quill/issues/4509
    // quill is removed, but this code is still in place for broken legacy emails
    basicEmailBody = basicEmailBody.replaceAll(/&nbsp;(?!<)|(?<!>)&nbsp;/g, ' ');

    return basicEmailBody;
  }

  // all email content elements are stored in json and therefore are being transformed to html elements which email clients support
  private static tranformContentElementsToMailHtml(previewCmsEmail: CmsContentItemContent): {
    textBody: string;
    footer: string;
  } {
    let textBody = '';
    let footer = '';
    const footerItem = previewCmsEmail.content?.find(
      content => content.type === 'email-footnotes'
    ) as CmsItem<'email-footnotes'>;

    for (const item of previewCmsEmail.content) {
      textBody += this.getEmailHtmlForContentItem(item, previewCmsEmail);
    }

    if (footerItem) {
      const fontSize = '9pt';
      const lineHeight = '11pt';
      footer += this.replaceTextElement(footerItem, fontSize, lineHeight);
    }

    footer += this.addFooterLegalText(previewCmsEmail);
    return { textBody, footer };
  }

  public static getEmailHtmlForContentItem(item: CmsItem, parentItem?: CmsContentItemContent): string {
    if (item.type === 'editor' || item.type === 'email-headline' || item.type === 'bullet-list') {
      return this.replaceTextElement(item);
    }
    if (item.type === 'single-or-multi-image') {
      return this.transformSingleOrMultiImageComponent(item);
    }
    if (item.type === 'video') {
      return this.transformVideoComponent(item);
    }
    if (item.type === 'spacer') {
      return this.transformSpacerComponent(item);
    }
    if (item.type === 'download-file') {
      return this.transformDownloadFileComponent(item);
    }
    if (item.type === 'email-button') {
      return this.transformEmailButtonComponent(item, parentItem);
    }
    if (item.type === 'email-quote') {
      return this.transformEmailQuoteComponent(item);
    }
    if (item.type === 'main-body-image-text') {
      return this.transformMainBodyImageText(item);
    }
    if (item.type === 'line') {
      return this.getLineComponentHtml(item);
    }
    if (item.type === 'index-list') {
      return CmsEmailIndexListElementHelper.getIndexListHtml(item);
    }

    return '';
  }

  private static transformSingleOrMultiImageComponent(item: CmsItem<'single-or-multi-image'>): string {
    let fileBody = `<table id="${item.id}" cellpadding="0" cellspacing="0" border="0" style="width: 100%; ${this.defaultPadding} background-color: ${
      item.backgroundColor
    };  text-align: center;"><tbody>
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
    ${this.getHtmlSpacerRow(item.paddingTop)}`;
    const paddingWidth = (item.content.length - 1) * 10;
    const imageWidth = this.defaultItemFullWidth / item.content?.length - paddingWidth;

    for (const [index, image] of item.content.entries()) {
      fileBody += `
      <td style="position: relative; display: inline-block; height: ${item.height ? `${item.height}px` : 'auto'}"> 
      <img alt="content-image" src="${image.link}" width="${imageWidth}" style="${
        item.content.length === 2 && index === 1
          ? 'margin-left: 10px;'
          : item.content.length === 3 && index === 1
            ? 'margin-left: 10px; margin-right: 10px;'
            : ''
      } height: 100%; width: ${imageWidth}px; max-width: ${imageWidth}px;">
      ${
        image?.anchorLink
          ? `<a style="position: absolute; height: 100%; width: 100%; top: 0; left: 0;" href="${image?.anchorLink}" rel="noopener noreferrer" target="_blank"></a>`
          : ''
      }</td>`;
    }

    return `${fileBody}${this.getHtmlSpacerRow(item.paddingBottom)}</tbody></table>`;
  }

  private static transformVideoComponent(item: CmsItem<'video'>): string {
    let fileBody = `<table id="${item.id}" cellpadding="0" cellspacing="0" border="0" style="width: 100%; ${this.defaultPadding}; text-align: center; background-color: ${
      item.backgroundColor
    };"><tbody>
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
    ${this.getHtmlSpacerRow(item.paddingTop)}`;

    const videoWidth = this.defaultItemFullWidth;
    const thumbnail = item.link.replace('originals', 'thumbnails').replace('.mp4', '.png');

    const video = `
      <video class="video" width="${videoWidth}" height="100%" style="height: 100%; width: ${videoWidth}px; max-width: ${videoWidth}px;" controls>
        <source src="${item.link}" type="video/mp4"/>
        <a rel="noopener" target="_blank" style="display: block; height: 100%; width: ${videoWidth}px; max-width: ${videoWidth}px;" href="${item.link}">
          <img width="100%" style="object-fit: contain; height: 100%; width: 100%" src="${thumbnail}"/>
        </a>
      </video>
    `;

    const outlookFallback = `
    <a class="videoFallback" style="display: none; mso-hide: all; height: 100%; width: ${videoWidth}px; max-width: ${videoWidth}px;" rel="noopener" target="_blank" href="${item.link}">
      <img ${item.height ? `height="${item.height}"` : ''} width="100%" border="0" src="${thumbnail}" style="object-fit: contain; height: ${item.height ? `${item.height}px` : 'auto'}; width: 100%">
    </a>
    `;

    fileBody += `
      <td style="position: relative; display: inline-block; height: ${item.height ? `${item.height}px` : 'auto'}"> 
        ${video}
        ${outlookFallback}
      </td>`;

    return `${fileBody}${this.getHtmlSpacerRow(item.paddingBottom)}</tbody></table>`;
  }

  private static transformDownloadFileComponent(item: CmsItem<'download-file'>): string {
    let fileBody = `<table id="${item.id}" cellpadding="0" cellspacing="0" border="0" style="width: 100%; ${this.defaultPadding}  background-color: ${
      item.backgroundColor
    };"><tbody>${this.getHtmlSpacerRow(item.paddingTop)} ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}<tr><td>`;
    for (const file of item.content) {
      fileBody += `
      <table
        cellpadding="0"
        cellspacing="0"
        border="0"
        style="width: 100%"
      >
        <tbody>
          <tr>
            <td>
              <tr style="font-size: ${item.fontSize}pt">
                <!--[if mso]>
                  <td style="width: 25px; border-top: 5px solid transparent;">
                <![endif]-->
                <!--[if !mso]> <!---->
                  <td style="width: 25px;">
                <!-- <![endif]-->

                <!--[if mso]>
                  <img
                  style="
                    width: 18px;
                    height: 18px;
                  "
                    src="${environment.coinImagePath}coin/images/originals/download_icon_SE.png"
                  />
                <![endif]-->
                <!--[if !mso]> <!---->
                  <img
                  style="
                    display:block;
                    width: 18px;
                    height: 18px;
                    margin: auto;
                  "
                    src="${environment.coinImagePath}coin/images/originals/download_icon_SE.png"
                  />
                <!-- <![endif]-->
                </td>
                <td>
                  <a
                    style="
                      color: #5e328b;
                    "
                    href="${file.link}"
                    ><span
                      style="${this.fontFamily}"
                      >${file.text}</span
                    ></a
                  >
                </td>
              </tr>
            </td>
          </tr>
        </tbody>
      </table>`;
    }
    return `${fileBody}</td></tr> ${this.getHtmlSpacerRow(item.paddingBottom)}</tbody></table>`;
  }

  private static transformSpacerComponent(item: CmsItem<'spacer'>): string {
    return `<table height="${item.height}" id="${item.id}" cellpadding="0" cellspacing="0" border="0" style="color: ${item.textColor}; background-color: ${
      item.backgroundColor
    }; width: 100%; height: ${item.height}px; max-height: ${item.height}px; ${this.defaultPadding}"><tbody>
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
    <tr>
      <td height="${item.height}" style="display: block; width: 100%; height: ${item.height}px; max-height: ${item.height}px; line-height: 2px;">&nbsp;</td></tr></tbody></table>`;
  }

  private static transformDefaultActionButton(
    item: CmsItem<'email-button'>,
    parentItem: CmsContentItemContent
  ): string {
    const buttonContent = `
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
        <a id="${item.id}" style="display: inline-block; border-radius: 20px; color: white; background: ${this.getButtonColorCode(parentItem)} padding-left: 16px; padding-right: 16px; 
        margin-top: 6px margin-bottom: 6px padding-top: 6px padding-bottom: 6px 
        ${this.fontFamily} font-size: 11pt; color: white; font-weight: bold; mso-padding-alt: 0;
        text-decoration: none; href="${item.content.link}">
        <!--[if mso]>
        <i style="letter-spacing: 16px; mso-font-width: -100%; mso-text-raise: 11pt;">&nbsp;</i>
        <![endif]-->
        <span align="center" style="mso-text-raise: 5pt; ${this.fontFamily} font-size: 11pt; line-height: 15pt;">${item.content.text} &#9002;</span>
        <!--[if mso]>
        <i style="letter-spacing: 16px; mso-font-width: -100%; mso-text-raise: 11pt;">&nbsp;</i>
        <![endif]-->
        </a>
 `;
    return buttonContent;
  }

  private static transformEmailButtonComponent(
    item: CmsItem<'email-button'>,
    parentItem?: CmsContentItemContent
  ): string {
    const containerTag = item.fullWidth ? 'div' : 'span';
    return `<table id="${item.id}" cellpadding="0" cellspacing="0" border="0" style="color: ${item.textColor}; ${this.defaultPadding} background-color: ${
      item.backgroundColor
    }; width: 100%;"><tbody>
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
    ${this.getHtmlSpacerRow(item.paddingTop, 1)}
    <tr>
      <td>
        <${containerTag} style="width: ${item.fullWidth ? '100%' : 'auto'}; border-radius: 20px;  background: ${this.getButtonColorCode(parentItem)} color: white; font-weight: bold; text-decoration: none; display: inline-block;
        margin-top: 6px; margin-bottom: 6px; padding-top: 6px; padding-bottom: 6px; mso-padding-alt: 0;
        ${item.fullWidth ? 'text-align: center;' : ''}">
          <a style="color: white; font-weight: bold; text-decoration: none; ${this.fontFamily} font-size: 11pt;
          margin-left: 16px; margin-right: 16px;" href="${item.content.link}">
            <!--[if mso]>
            <i style="letter-spacing: 16px; mso-font-width: -100%; mso-text-raise: 11pt;">&nbsp;</i>
            <![endif]-->
            <span  align="center" style="mso-text-raise: 5pt; ${this.fontFamily} font-size: 11pt; line-height: 15pt;">${item.content.text} &#9002;</span>
            <!--[if mso]>
            <i style="letter-spacing: 16px; mso-font-width: -100%; mso-text-raise: 11pt;">&nbsp;</i>
            <![endif]-->
          </a>
        </${containerTag}>
      </td>
    </tr>
    ${this.getHtmlSpacerRow(item.paddingBottom, 1)}
    </tbody></table>
 `;
  }

  private static getButtonColorCode(item?: CmsContentItemContent): string {
    return item?.sideData.isGenericBranding ? '#4D217A;' : '#8A00E5;';
  }

  private static transformEmailQuoteComponent(item: CmsItem<'email-quote'>): string {
    const buttonContent = `<table id="${item.id}" cellpadding="0" cellspacing="0" border="0" style="width: 100%; color: ${item.textColor}; background-color: ${
      item.backgroundColor
    }; ${this.defaultPadding}"><tbody>
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
    ${this.getHtmlSpacerRow(item.paddingTop, 3)}
    <tr>
      <td style="vertical-align: top; padding: 0">
          <div style="
            text-align: right;
            line-height: initial;
            font-size: 66px;
            ${this.fontFamily} 
            color: #5E328B;">&ldquo;</div>
      </td>
      <td style="${this.fontFamily} padding: 0">
          <p style="font-size: 11pt; text-align: center;">
            <span style="${this.fontFamily} font-size: 11pt; line-height: 15pt;">${item.content.text}</span>
          </p>
      </td>
      <td style="vertical-align: bottom; padding: 0">
        <span style="
          text-align: left;
          line-height: normal;
          font-size: 66px;
          ${this.fontFamily} 
          color: #5E328B;">„</span>
      </td>    
    </tr>
    ${this.getHtmlSpacerRow(item.paddingBottom, 3)}
    </tbody></table>
    `;
    return buttonContent;
  }

  private static transformMainBodyImageText(item: CmsItem<'main-body-image-text'>): string {
    const content = [
      `<td style="width: 33%; height: 150px;">
        <img alt="main-body-image" width="100%" style="width: 100%; height: 100%;" src="${item.content.img}" >
        ${
          item?.content?.link
            ? `<a style="position: absolute; height: 100%; width: 100%; top: 0; left: 0;" href="${item?.content?.link}" rel="noopener noreferrer" target="_blank"></a>`
            : ''
        }
      </td>`,
      `<td style="width: 2%;"></td>`,
      `<td style="width: 65%;">
          <span style="${this.fontFamily} font-size: 11pt; line-height: 15pt;">${item.content.text}</span>
      </td>`
    ];

    if (item.inverted) content.reverse();

    return `<table id="${item.id}" style="color: ${item.textColor}; background-color: ${item.backgroundColor}; ${
      this.defaultPadding
    }" border="0" width="auto" cellpadding="0" cellspacing="0" ><tbody>
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
    ${this.getHtmlSpacerRow(item.paddingTop)}
    <tr>
      <td>${this.addGhostTable(content.join(''))}</td>
    </tr>
    ${this.getHtmlSpacerRow(item.paddingBottom)}
  </tbody></table>
 `;
  }

  private static replaceTextElement(
    item: CmsItem<'email-headline' | 'editor' | 'bullet-list' | 'email-footnotes'>,
    fontSize?: string,
    lineHeight?: string
  ): string {
    let existingContent = item.content;

    // the quill editor removes wrapping div tags, so we enforce them here
    if (!existingContent?.startsWith('<div')) {
      existingContent = `<div>${existingContent}</div>`;
    }

    existingContent = existingContent?.replaceAll(
      '<p>',
      `<p style="margin: 0; ${item.isHighlighted ? 'text-align:center;' : ''}"><span style="${this.fontFamily} font-size: ${fontSize ?? '11pt'}; line-height: ${
        lineHeight ?? '15pt'
      };">`
    );
    existingContent = existingContent?.replaceAll('</p>', '</span></p>');
    existingContent = existingContent?.replaceAll(
      '<li>',
      `<li style="margin: 0"><span style="${this.fontFamily} font-size: ${fontSize ?? '11pt'}; line-height: ${lineHeight ?? '15pt'};">`
    );
    existingContent = existingContent?.replaceAll('</li>', '</span></li>');

    existingContent = existingContent?.replaceAll('<sup>', '<sup style="line-height: 0">');
    existingContent = existingContent?.replaceAll('<sub>', '<sub style="line-height: 0">');
    existingContent = existingContent?.replaceAll('<ul>', '<ul style="margin: 0;">');
    existingContent = existingContent?.replaceAll('<ol>', '<ol style="margin: 0;">');

    existingContent = existingContent?.replaceAll(
      '<h2>',
      `<h2 style="margin: 0; font-size: 18pt; line-height: 21pt;"><span style="${this.fontFamily}">`
    );
    existingContent = existingContent?.replaceAll('</h2>', `</span></h2>`);
    existingContent = existingContent?.replaceAll(
      '<h4>',
      `</span><h4 style="margin: 5pt 0 0; font-size: 12pt; line-height: 21pt;"><span style="${this.fontFamily}">`
    );
    existingContent = existingContent?.replaceAll('</h4>', '</span></h4>');

    return existingContent
      ?.replace(
        '<div',
        `<table id="${item.id}" cellpadding="0" cellspacing="0" border="0" style="${this.defaultPadding} width: 100%; color: ${item.textColor}; background-color: ${
          item.backgroundColor
        };"><tbody>
        ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
        ${this.getHtmlSpacerRow(item.paddingTop)}
        <tr><span style="${this.fontFamily} font-size: ${fontSize ?? '11pt'}; line-height: ${lineHeight ?? '15pt'};"><td style="display: block; overflow:hidden; font-size: ${
          fontSize ?? '11pt'
        }; line-height: ${lineHeight ?? '15pt'};"><table width="100%"><td style="padding: 8px; ${item.isHighlighted ? 'border:2px solid #8A00E5; color: #8A00E5;' : ''}"`
      )
      ?.replace(
        '</div>',
        `</td></table></td></span></tr>
        ${this.getHtmlSpacerRow(item.paddingBottom)}
        </tbody></table>`
      );
  }

  private static getLineComponentHtml(item: CmsItem<'line'>): string {
    const paddingStyle = item.fullWidth ? 'padding-left: 0; padding-right: 0;' : '';

    return `<table id="${item.id}" border="0" width="100%" cellpadding="0" cellspacing="0" style="color: ${item.textColor}; background-color: ${item.backgroundColor}; ${
      this.defaultPadding
    }; ${paddingStyle}"><tbody>
    ${this.getMicrosoftOutlookAnchorReferenceTag(item.id)}
    ${this.getHtmlSpacerRow(item.paddingTop)}
      <tr>
        <td style="background:none; border-bottom: 2px solid #DEE4EF; height:2px; width:100%; margin:0px 0px 0px 0px;">&nbsp;</td>
      </tr>
    ${this.getHtmlSpacerRow(item.paddingBottom)}
    </tbody></table>
    `;
  }

  private static getHeadline(item: CmsContentItemContent): string {
    return `${this.getHtmlSpacerRow(item.paddingTop)}<td id="${item.id}" style="${this.defaultPadding} padding-top: 20px;
    padding-bottom: 20px; display: block; background-color: ${item.sideData.backgroundColor}; color: ${item.sideData.textColor};">
                <h1 style="font-size: 24pt; margin: 0; ${this.fontFamily} font-weight: bold;"><span style="${this.fontFamily}">${item.sideData.mainHeadline ?? ''}</span></h1>
                ${this.getSubHeadline(item)}
                ${this.showDefaultActionButtonInPreview(item)}
            </td>
  ${this.getHtmlSpacerRow(item.paddingBottom)}`;
  }

  private static showDefaultActionButtonInPreview(item: CmsContentItemContent): string {
    return item.sideData.actionButtonLink || item.sideData.actionButtonText
      ? this.transformDefaultActionButton(
          {
            type: 'email-button' as const,
            paddingTop: 10,
            paddingBottom: 10,
            content: {
              text: item.sideData.actionButtonText,
              link: item.sideData.actionButtonLink
            }
          },
          item
        )
      : '';
  }

  private static getSubHeadline(item: CmsContentItemContent): string {
    return item.sideData.subHeadline
      ? `<h1 style="font-size: 13pt; margin: 10px 0 10px; ${this.fontFamily} ">${item.sideData.subHeadline ?? ''}</h1>`
      : '';
  }

  private static addFooterLegalText(previewCmsEmail: CmsContentItemContent): string {
    return `
    <table border="0" width="100%" cellpadding="0" cellspacing="0" style="${this.defaultPadding} width: 100%;"><tbody>
     ${this.getHtmlSpacerRow(10)}
      <tr>
        <td>
          <p style="${this.fontFamily}  color: #AAA6C0; font-size: 9pt; margin: 0;"><span style="color: white; ${
            this.fontFamily
          } line-height: 11pt;">siemens-energy.com </span><span style="${this.fontFamily} line-height: 11pt;">Restricted &copy; ${previewCmsEmail.sideData.isGenericBranding ? 'Siemens Energy and Gamesa Renewable Energy' : 'Siemens Energy'}, ${new Date().getFullYear()}</span></p>
        </td>
      </tr>
    </tbody></table>`;
  }

  public static getHtmlSpacerRow(height: number | string, amountOfTableDataElements = 1): string {
    const usedHeight = typeof height === 'string' ? Number.parseFloat(height) : height;
    const heightInPt = Math.round((usedHeight ?? 0) * 0.75);

    let tdElements = '';

    for (let i = 0; i < amountOfTableDataElements; i++) {
      tdElements += ` <td style="height: ${heightInPt}pt; line-height: 2px;">&nbsp;</td>`;
    }

    const row = usedHeight
      ? `
      <tr>
        ${tdElements}
      </tr>`
      : '';

    return row;
  }

  public static getMicrosoftOutlookAnchorReferenceTag(id: string): string {
    return `<a style="height: 0; line-height: 0;" name="${id}"></a>`;
  }

  public static addGhostTable(content: string): string {
    return `
    <!--[if mso]>
      <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
        <tbody>
          <tr>
    <![endif]-->
            ${content}
    <!--[if mso]>
          </tr>
        </tbody>
      </table>
    <![endif]-->`;
  }

  private static getHeaderLogo(item: CmsContentItemContent): string {
    return item.sideData.isGenericBranding
      ? `<div style="height: 74px"></div>`
      : `<img width="140" height="74" style="width:1.4583in;height:.7708in" id="Bild_x0020_3" src="data:image/png;base64,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">`;
  }
}
